import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import {
    changeScrollBehavior,
    currencyFormatter,
    digits,
    getDecimalPartCount,
} from 'src/common/helpers';
import { EyeClose, EyeOpen, Transfer } from 'src/components/core/icons';
import { FlexContainer, StyledFlexContainer } from 'src/components/core/layout/FlexContainer';
import { useAppDispatch, useAppSelector, useHiddenAssets } from 'src/hooks';
import { fetchAvailableAssets } from 'src/setup/redux/slices/spotSlice';
import { SpotAsset } from 'src/types';

import { ErrorBoundary } from '../../common/error/ErrorBoundary';
import { TransferModal } from '../../common/modals/transferModal';

const StyledAsset = styled(StyledFlexContainer)`
    padding: 16px 11px;
    flex-direction: column;
    ${({ theme: { colors } }) => `
        border-bottom: 1px solid ${colors.border.light};
        border-top: 1px solid ${colors.border.light};
    `}
    & > div {
        margin-top: 8px;
        &:first-of-type {
            margin-bottom: 8px;
            margin-top: 0;
        }
    }
`;

const LabelText = styled(StyledFlexContainer)<{ color: string }>`
    ${({ theme: { typography }, color }) => `
        font-size: ${typography.size.desktop.paragraph.p3}; 
        line-height: calc(${typography.lineHeight[2]} - 1px)};
        &.heading {
            font-size: ${typography.size.desktop.paragraph.p2};
            line-height: calc(${typography.lineHeight[3]} - 0.5px);
            font-weight: ${typography.weight.medium}
        }
        color: ${color}
    `}
`;

const Asset: FC = () => {
    const { t } = useTranslation();
    const { colors } = useTheme();
    const dispatch = useAppDispatch();

    const { assets, error, loading } = useAppSelector((state) => state.spot);
    const { currentSymbol } = useAppSelector((state) => state.spot);
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
    const [availableUSDT, setAvailableUSDT] = useState({} as SpotAsset);
    const [availableCurrentTokenBalance, setAvailableCurrentTokenBalance] = useState(
        {} as SpotAsset,
    );
    const { isAssetsHidden, toggleIsAssetsHidden } = useHiddenAssets();

    const fetchAvailableAssetsError = error?.fetchAvailableAssets !== undefined;
    const fetchAvailableAssetsLoading = loading?.fetchAvailableAssets;

    const fetchSpotAvailableAssets = () => {
        dispatch(fetchAvailableAssets());
    };

    const handleAssetsVisibility = () => {
        toggleIsAssetsHidden();
    };

    const handleTransferModal = () => {
        setIsTransferModalOpen(!isTransferModalOpen);
        changeScrollBehavior(!isTransferModalOpen);
    };

    useEffect(() => {
        const availableUSDT = assets.find(
            (asset: SpotAsset) => asset.tokenId === currentSymbol.quoteTokenId,
        );
        setAvailableUSDT(availableUSDT as SpotAsset);
        const availableCurrentTokenBalance = assets.find(
            (asset: SpotAsset) => asset.tokenId === currentSymbol.baseTokenId,
        );
        setAvailableCurrentTokenBalance(availableCurrentTokenBalance as SpotAsset);
    }, [assets, currentSymbol]);

    return (
        <>
            <StyledAsset>
                <FlexContainer styles={{ width: '100%', justifyContent: 'space-between' }}>
                    <FlexContainer styles={{ gap: 4 }}>
                        <LabelText className={'heading'} color={colors.text.primary}>
                            {t('assets')}
                        </LabelText>
                        {isAssetsHidden ? (
                            <EyeClose
                                fill={colors.text.tertiary}
                                className={'clickable'}
                                onClick={handleAssetsVisibility}
                            />
                        ) : (
                            <EyeOpen
                                fill={colors.text.tertiary}
                                className={'clickable'}
                                onClick={handleAssetsVisibility}
                            />
                        )}
                    </FlexContainer>
                    <FlexContainer
                        styles={{ gap: 8 }}
                        className={'clickable Transfer'}
                        onClick={handleTransferModal}
                    >
                        <Transfer stroke={colors.primary} />
                        <LabelText color={colors.primary}>{t('transfer')}</LabelText>
                    </FlexContainer>
                </FlexContainer>
                <ErrorBoundary
                    hasError={fetchAvailableAssetsError}
                    loading={fetchAvailableAssetsLoading}
                    retryAction={fetchSpotAvailableAssets}
                >
                    <FlexContainer styles={{ width: '100%', justifyContent: 'space-between' }}>
                        <FlexContainer>
                            <LabelText color={colors.text.tertiary}>
                                {t('token_available', { token: currentSymbol.quoteTokenId })}
                            </LabelText>
                        </FlexContainer>
                        <FlexContainer>
                            <LabelText color={colors.text.primary}>
                                {isAssetsHidden
                                    ? `----`
                                    : availableUSDT
                                    ? currencyFormatter(
                                          digits(
                                              Number(availableUSDT.free),
                                              getDecimalPartCount(Number(availableUSDT.free)),
                                          ),
                                          getDecimalPartCount(Number(availableUSDT.free)),
                                          getDecimalPartCount(Number(availableUSDT.free)),
                                      )
                                    : '0.0'}{' '}
                                {currentSymbol.quoteTokenId}
                            </LabelText>
                        </FlexContainer>
                    </FlexContainer>
                    <FlexContainer styles={{ width: '100%', justifyContent: 'space-between' }}>
                        <FlexContainer>
                            <LabelText color={colors.text.tertiary}>
                                {t('token_available', { token: currentSymbol.baseTokenId })}
                            </LabelText>
                        </FlexContainer>
                        <FlexContainer>
                            <LabelText color={colors.text.primary}>
                                {isAssetsHidden
                                    ? `----`
                                    : availableCurrentTokenBalance
                                    ? currencyFormatter(
                                          digits(
                                              Number(availableCurrentTokenBalance.free),
                                              getDecimalPartCount(
                                                  Number(availableCurrentTokenBalance.free),
                                              ),
                                          ),
                                          getDecimalPartCount(
                                              Number(availableCurrentTokenBalance.free),
                                          ),
                                          getDecimalPartCount(
                                              Number(availableCurrentTokenBalance.free),
                                          ),
                                      )
                                    : '0.0'}{' '}
                                {currentSymbol.baseTokenId}
                            </LabelText>
                        </FlexContainer>
                    </FlexContainer>
                </ErrorBoundary>
            </StyledAsset>
            {isTransferModalOpen && (
                <TransferModal open={isTransferModalOpen} handleClose={handleTransferModal} />
            )}
        </>
    );
};

export { Asset };
